<template>
  <app-ag-grid
    title="Users"
    :columnDefs="columnDefs"
    :rowData="users"
    :loading="loading"
    :getRowStyle="getRowStyle"
    :mobileCard="mobileCard"
    @refresh="fetchUsers"
    @add="onAdd"
    @row-dbl-click="(data) => $router.push({ path: `users/${data._id}/edit` })"
  ></app-ag-grid>
</template>

<script>
import AppAgGrid from "@/components/common/ag-grid.vue";

export default {
  name: "UsersPage",
  components: { AppAgGrid },

  data() {
    return {
      loading: false,

      columnDefs: [
        {
          headerName: "View",
          field: "_id",
          width: 80,
          cellRenderer: (params) => {
            return `
              <i
                class="mdi mdi-eye view-user"
                data-id="${params.data._id}"
                title="View User"
                style="
                  cursor: pointer;
                  font-size: 18px;
                  color: #0a5a90;
                "
              ></i>
            `;
          },

          onCellClicked: (params) => {
            if (params.event.target.classList.contains("view-user")) {
              this.$router.push({ path: `users/${params.data._id}/view` });
            }
          },
        },
        { headerName: "Name", field: "name" },
        { headerName: "Email", field: "email", width: 300 },
        { headerName: "Phone", field: "phone" },
        { headerName: "User Type", field: "userType" },

        {
          headerName: "Role",
          field: "teamRole",
          width: 130,
          valueFormatter: (p) => {
            if (p.data?.ownerUserId) {
              return p.value === "VIEW_ONLY" ? "View Only" : "Full Access";
            }
            return "Owner";
          },
        },

        {
          headerName: "Owner",
          field: "ownerUserId",
          width: 220,
          valueFormatter: (p) => {
            if (!p.value) return "-";
            return `${p.value.name || "-"} (${p.value.email || "-"})`;
          },
        },

        {
          // Blank for a team-member row — team size is only meaningful
          // on the owner's own row. Flagged so a large team (which may be
          // legitimate, or may be one paying account quietly used by an
          // unrelated business — see Terms & Conditions Section 4) is easy
          // to spot while scanning the list, without a dedicated report.
          headerName: "Team Size",
          field: "_id",
          width: 130,
          valueGetter: (p) => (p.data?.ownerUserId ? null : this.teamSizeFor(p.data?._id)),
          valueFormatter: (p) => (p.value == null ? "-" : String(p.value)),
          cellStyle: (p) => {
            if (p.value == null || p.value <= 2) return null;
            return { backgroundColor: "#fff3d6", color: "#b8720a", fontWeight: "600" };
          },
        },

        {
          headerName: "Payment Expiry",
          field: "paymentExpiryDate",

          valueFormatter: (p) => {
            if (!p.value) return "-";

            const date = new Date(p.value);
            if (isNaN(date.getTime())) return "-";

            const dd = String(date.getDate()).padStart(2, "0");
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const yyyy = date.getFullYear();

            return `${dd}-${mm}-${yyyy}`;
          },

          cellStyle: (params) => {
            if (!params.value) return null;

            const expiryDate = new Date(params.value);
            if (isNaN(expiryDate.getTime())) return null;

            const today = new Date();
            const diffTime = expiryDate.getTime() - today.getTime();
            const diffDays = diffTime / (1000 * 60 * 60 * 24);

            if (diffDays <= 5 && diffDays >= 0) {
              return {
                backgroundColor: "#ffebee",
                color: "#c62828",
                fontWeight: "600",
              };
            }

            return null;
          },
        },

        {
          headerName: "Created At",
          field: "createdAt",
          valueFormatter: (p) => {
            if (!p.value) return "-";

            const date = new Date(p.value);
            if (isNaN(date.getTime())) return "-";

            const dd = String(date.getDate()).padStart(2, "0");
            const mm = String(date.getMonth() + 1).padStart(2, "0");
            const yyyy = date.getFullYear();

            return `${dd}-${mm}-${yyyy}`;
          },
        },
      ],
    };
  },

  computed: {
    users() {
      return this.$store.state.users.users;
    },

    mobileCard() {
      return {
        title: (row) => row.name || "-",
        subtitle: (row) => row.email || "-",
        badge: (row) => ({
          text: row.userType || "-",
          style:
            row.userType === "admin"
              ? { background: "#fdeaf1", color: "#b8355f" }
              : { background: "#eef3fb", color: "#0a5a90" },
        }),
        fields: (row) => [
          { label: "Phone", value: row.phone || "-" },
          { label: "Expiry", value: this.formatDate(row.paymentExpiryDate) },
          { label: "Role", value: this.roleLabel(row) },
          ...(row.ownerUserId
            ? [{ label: "Owner", value: `${row.ownerUserId.name || "-"} (${row.ownerUserId.email || "-"})` }]
            : [{ label: "Team Size", value: String(this.teamSizeFor(row._id)) }]),
        ],
        actions: (row) => [
          {
            label: "View",
            icon: "mdi-eye-outline",
            color: "#0a5a90",
            onClick: () => this.$router.push({ path: `users/${row._id}/view` }),
          },
          {
            label: "Edit",
            icon: "mdi-pencil-outline",
            color: "#475569",
            onClick: () => this.$router.push({ path: `users/${row._id}/edit` }),
          },
        ],
      };
    },
  },

  created() {
    this.fetchUsers();
  },

  methods: {
    async fetchUsers() {
      this.loading = true;
      try {
        await this.$store.dispatch("users/getUsers");
      } finally {
        this.loading = false;
      }
    },

    onAdd() {
      this.$router.push({ path: "users/create" });
    },

    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "-";
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    },

    roleLabel(row) {
      if (!row.ownerUserId) return "Owner";
      return row.teamRole === "VIEW_ONLY" ? "View Only" : "Full Access";
    },

    // Counts how many team members (any status) belong to the given
    // owner id — computed client-side from the already-fetched full user
    // list rather than a separate endpoint, since admin already loads
    // every user up front.
    teamSizeFor(ownerId) {
      if (!ownerId) return 0;
      return this.users.filter((u) => u.ownerUserId && u.ownerUserId._id === ownerId).length;
    },

    getRowStyle(params) {
      const expiry = params.data && params.data.paymentExpiryDate;
      if (!expiry) return null;

      const expiryDate = new Date(expiry);
      if (isNaN(expiryDate.getTime())) return null;

      if (expiryDate.getTime() < Date.now()) {
        return { backgroundColor: "#fdecea" };
      }

      return null;
    },
  },
};
</script>
